import apiClient from "./client";
import { ENDPOINTS } from "./endpoints";
import { getUserIds, getToken } from "./session";
import { BASE_URL, API_KEY } from "./constants";
import { ENCRYPTION_ENABLED, ENCRYPT_RESPONSE_HEADER, encryptPayload } from "./encryption";

export const createProspect = (payload) =>
  apiClient.post(ENDPOINTS.applicationForm.createProspect, { source: "SFA", ...getUserIds(), ...payload });

export const addFinancier = (payload) =>
  apiClient.post(ENDPOINTS.applicationForm.addFinancier, { source: "SFA", ...getUserIds(), ...payload });

// tabs defaults (server-side) to ["lead_details"] when omitted — pass
// "finanicer_details" too when restoring a lead that's already past step 2.
// The per-financier tabs ("noa_received", "contract_signed_details") also
// need financierId.
export const getLeadDetails = (leadId, tabs, financierId) =>
  apiClient.post(ENDPOINTS.applicationForm.getLeadDetails, {
    source: "SFA",
    ...getUserIds(),
    lead_id: leadId,
    ...(financierId != null ? { financier_id: financierId } : {}),
    ...(tabs?.length ? { tab_name: tabs } : {}),
  });

export const uploadDoc = (formData) => {
  const { user_id, role_id } = getUserIds();
  formData.append("source", "SFA");
  formData.append("user_id", user_id);
  formData.append("role_id", role_id);
  return apiClient.post(ENDPOINTS.applicationForm.uploadDoc, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const getDocs = (leadId, financierId) =>
  apiClient.post(ENDPOINTS.applicationForm.getDocs, {
    source: "SFA",
    ...getUserIds(),
    lead_id: leadId,
    financier_id: financierId,
  });

// Persists steps 4 (property_loan_details) and 5 (customer_coborrower_details)
// — the only two form_type values relevant here; lead_details (step 1) is
// handled by create_prospect instead. save_type "final" (the default, used by
// the explicit Next/Submit click) enforces the DTO's full validation and
// stamps status; "partial" (autosave — see saveLeadDataBeacon and
// useApplicationForm's autosave effect) skips that for a draft save.
// save_docs takes a mixed lead_docs list: new rows ({ doc_id, parent_id,
// doc_path, upload_type }) and soft-deletes (status 2) of existing ones by
// their rem_lead_docs id ({ id, action: "remove" }) — the get_docs row id,
// not the document_list doc_id.
export const saveDocs = (leadId, leadDocs) =>
  apiClient.post(ENDPOINTS.applicationForm.saveDocs, {
    source: "SFA",
    ...getUserIds(),
    lead_id: leadId,
    lead_docs: leadDocs,
  });

export const removeDocs = (leadId, ids) => saveDocs(leadId, ids.map((id) => ({ id, action: "remove" })));

export const saveLeadData = (leadId, formType, details, saveType = "final") =>
  apiClient.post(ENDPOINTS.applicationForm.saveLeadData, {
    source: "SFA",
    ...getUserIds(),
    lead_id: leadId,
    form_type: formType,
    save_type: saveType,
    last_form_filled: formType,
    [formType]: details,
  });

  // Same request as saveLeadData(..., "partial"), fired via fetch(keepalive)
// instead of axios/XHR — the only way a request reliably finishes once the
// tab is actually closing; an in-flight XHR gets aborted right there, a
// keepalive fetch is kept alive by the browser past unload. Fire-and-forget:
// there's nothing to await once the page is on its way out.
export const saveLeadDataBeacon = (leadId, formType, details) => {
  const token = getToken();
  const headers = { "Content-Type": "application/json", api_source: "WEBVIEW" };
  if (token) headers.Authorization = `Bearer ${token}`;
  else if (API_KEY) headers.apikey = API_KEY;
  const payload = {
    source: "SFA",
    ...getUserIds(),
    lead_id: leadId,
    form_type: formType,
    save_type: "partial",
    last_form_filled: formType,
    [formType]: details,
  };
  // Encrypted like every apiClient POST (see ./encryption).
  if (ENCRYPTION_ENABLED) headers[ENCRYPT_RESPONSE_HEADER] = 1;
  const body = JSON.stringify(ENCRYPTION_ENABLED ? encryptPayload(payload) : payload);
  try {
    // BASE_URL may end with "/" (REACT_APP_GETWAY_API) and every path starts
    // with "/" — trim one so the URL has no "//" (the gateway 404s on it).
    // axios (apiClient) joins these itself; a raw fetch does not.
    const url = BASE_URL.replace(/\/+$/, "") + ENDPOINTS.applicationForm.saveLeadData;
    fetch(url, { method: "POST", headers, body, keepalive: true });
  } catch {
    /* best-effort — page is unloading */
  }
};

// NOA (Notice of Approval) and Contract Signing saves, one financier per call.
// Responses are always HTTP 200 with the real outcome in body.status
// (200 / 400 / 500) and body.message. Saved values are read back through
// getLeadDetails' "noa_received" / "contract_signed_details" tabs.
export const saveNoa = (payload) =>
  apiClient.post(ENDPOINTS.postApplication.saveNoa, { source: "SFA", ...getUserIds(), ...payload });

// Reads NOA details from an uploaded NOA / email screenshot (OCR). Nothing is
// saved — body.data.fields holds NOA form values to pre-fill for review.
export const extractNoa = (payload) =>
  apiClient.post(ENDPOINTS.postApplication.extractNoa, { source: "SFA", ...getUserIds(), ...payload });

export const saveContract = (payload) =>
  apiClient.post(ENDPOINTS.postApplication.saveContract, { source: "SFA", ...getUserIds(), ...payload });

// Per-financier "Send to Bank" state for a lead: recipients, the two separate
// email templates (template/subject for the first send, follow_up_* for the
// reminder), document_categories (the docs attached for that financier) and
// history (newest first; template_for "sent_to_financier" | "follow_up_email").
// financier_id is required (400 "financier_id must be an integer number").
export const getSentToFinancier = (leadId, financierId) =>
  apiClient.post(ENDPOINTS.postApplication.getSentToFinancier, {
    source: "SFA",
    ...getUserIds(),
    lead_id: leadId,
    financier_id: financierId,
  });

// Records a send to one financier and emails it. The same endpoint covers both
// emails: the first send has no follow_up_email (it takes endorsement_date /
// stf_remark); the follow-up sends follow_up_email "Yes" with follow_up_date /
// stf_follow_up_remark.
export const saveSentToFinancier = (payload) =>
  apiClient.post(ENDPOINTS.postApplication.sentToFinancier, { source: "SFA", ...getUserIds(), ...payload });

// Lead-level remark history. save_remark tags each remark with the stage it was
// added from (tab_name); get_remarks returns every remark on the lead, newest first.
export const saveRemark = (leadId, tabName, remark) =>
  apiClient.post(ENDPOINTS.postApplication.saveRemark, { source: "SFA", lead_id: leadId, tab_name: tabName, remark });

export const getRemarks = (leadId) => apiClient.post(ENDPOINTS.postApplication.getRemarks, { source: "SFA", lead_id: leadId });

// Per-bank actions. Each responds { status, message, data } where data is the
// lead's status snapshot: lead flags (is_lost, can_cancel_application, …) plus
// financiers[] with the same keys as get_leads' lead_financiers.
export const deferFinancier = (leadId, financierId, remark) =>
  apiClient.post(ENDPOINTS.postApplication.deferFinancier, {
    source: "SFA", ...getUserIds(), lead_id: leadId, financier_id: Number(financierId), remark,
  });

export const resumeFinancier = (leadId, financierId) =>
  apiClient.post(ENDPOINTS.postApplication.resumeFinancier, {
    source: "SFA", ...getUserIds(), lead_id: leadId, financier_id: Number(financierId),
  });

export const cancelFinancier = (leadId, financierId, remark) =>
  apiClient.post(ENDPOINTS.postApplication.cancelFinancier, {
    source: "SFA", ...getUserIds(), lead_id: leadId, financier_id: Number(financierId), remark,
  });

// Cancel reasons + every bank with its status, for the Cancel application sheet.
export const getCancelDetails = (leadId) =>
  apiClient.post(ENDPOINTS.postApplication.getCancelDetails, { source: "SFA", ...getUserIds(), lead_id: leadId });

// Cancel application = Mark as Lost (the same API Loanbox uses; Loanbox's
// Re-open brings it back). reasonId is one of get_cancel_details.reasons.
// Every bank then reads as Cancelled.
export const cancelApplication = (leadId, reasonId, remark, financierId) =>
  apiClient.post(ENDPOINTS.postApplication.markLeadStatus, {
    source: "SFA",
    ...getUserIds(),
    lead_id: leadId,
    action_type: "lost",
    sub_status_id: Number(reasonId),
    remark,
    ...(financierId ? { financier_id: Number(financierId) } : {}),
  });

// create_prospect's real response is { lead_id, customer_id, rem_loan_id (a
// congratulations message, not an id despite the name), loan_code }. Every other
// lead endpoint (add_financier, get_docs, document_list, get_lead_details) wants
// the raw numeric lead_id, not the formatted display code — so this returns the
// raw id, and formatLoanCode() below builds the "REM123" string for display only.
export const extractProspectId = (data, fallback) => {
  const created = data?.data || {};
  return created.lead_id ?? created.loan_id ?? created.loanId ?? created.prospect_id ?? created.id ?? fallback;
};

// Mirrors the backend's buildLoanCode(leadId) — "REM" + the raw numeric lead id.
export const formatLoanCode = (id) => (id ? "REM" + id : "");
